<template>
  <div class="pt-16 lg:pt-0">
    <a id="booking-form" ref="bookingForm"></a>
    <div  v-if="page === 'book'" class="mt-10 lg:-mt-6 sm:px-8">
      <a href="#back" @click.prevent="back()" class="text-[18px] flex items-center text-sm text-dark hover:underline" aria-label="Go Back">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="relative -top-[2px]">
          <path d="M7.77252 11.1973L14.622 4.34783C15.0501 3.88406 15.7993 3.88406 16.2274 4.34783C16.6911 4.77592 16.6911 5.52508 16.2274 5.95318L10.1984 12.0178L16.2274 18.0468C16.6911 18.4749 16.6911 19.2241 16.2274 19.6522C15.7993 20.1159 15.0501 20.1159 14.622 19.6522L7.77252 12.8027C7.30875 12.3746 7.30875 11.6254 7.77252 11.1973Z" fill="#52535D"/>
        </svg>
        Go Back
      </a>
    </div>
    <loader v-if="loading" />
    <load-error v-if="!loading && loadFailed" class="my-10" @retry="loadCatalog" />
    <book :code="code"
          v-if="page === 'book' && !loadFailed"
          @top="top"
          @setPage="setPage"></book>
    <thanks v-if="page === 'thanks'"></thanks>
  </div>
</template>
<script>
import "./helpers/form-setup"
import Loader from "./components/Loader.vue";
import LoadError from "../checkout/components/LoadError.vue";
import { reportLoadError } from "../helpers/network";
import { Book, Thanks } from "./routes"
import useStore from "./store"
import { mapActions, mapState } from "pinia";

export default {
  name: 'BookWidget',
  components: {
    Loader,
    LoadError,
    Book,
    Thanks
  },
  props: {
    code: {
      type: [String, Number, Boolean],
      default: false,
    },
    initialPage: {
      type: [String, Number, Boolean],
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadFailed: false,
      page: 'book'
    };
  },
  computed: {
    ...mapState(useStore, ["booking", "bookingToken"]),
  },
  methods: {
    preservedQueryParams() {
      const currentParams = new URLSearchParams(window.location.search);
      const preservedParams = new URLSearchParams();

      ["booking_mode", "booking_smoke", "booking_key"].forEach((key) => {
        if (currentParams.has(key)) {
          preservedParams.set(key, currentParams.get(key));
        }
      });

      return preservedParams;
    },
    baseBookingPath() {
      return window.location.pathname.replace(/\/thanks\/?$/, "").replace(/\/$/, "") || "";
    },
    thanksPath() {
      return `${this.baseBookingPath()}/thanks/`;
    },
    setLoading(loading) {
      this.loading = loading;
    },
    syncPageWithLocation() {
      const onThanksPath = window.location.pathname.replace(/\/$/, "").endsWith("/thanks");
      this.page = onThanksPath ? "thanks" : "book";
    },
    pushThanksUrl() {
      const bookingNumber = this.booking?.internal_id ?? this.booking?.internal_id ?? "";
      const token = this.bookingToken ?? "";

      if (!bookingNumber || !token) {
        return;
      }

      const params = this.preservedQueryParams();
      params.set("booking", bookingNumber);
      params.set("token", token);
      const nextUrl = `${this.thanksPath()}?${params.toString()}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;

      if (currentUrl === nextUrl) {
        return;
      }

      window.history.pushState({ page: "thanks" }, "", nextUrl);
    },
    copyQuoteThanks(){
      this.copyQuoteToBooking();
      this.page = 'thanks';
      this.pushThanksUrl();
    },
    setPage(page){
      this.page = page;

      if (page === "thanks") {
        this.pushThanksUrl();
        return;
      }

      if (page === "book") {
        const nextUrl = this.baseBookingPath() || "/";
        const params = this.preservedQueryParams();
        const nextUrlWithQuery = params.toString() ? `${nextUrl}?${params.toString()}` : nextUrl;
        const currentUrl = `${window.location.pathname}${window.location.search}`;

        if (currentUrl !== nextUrlWithQuery) {
          window.history.pushState({ page: "book" }, "", nextUrlWithQuery);
        }
      }
    },
    top(){
        setTimeout(() => {
          this.$refs.bookingForm.scrollIntoView({
            behavior: 'smooth',
          });
        }, 300)
    },
    back(){
      window.history.back();
    },
    ...mapActions(useStore, ['copyQuoteToBooking']),
    async loadCatalog() {
      const store = useStore();

      this.loading = true;
      this.loadFailed = false;

      try {
        await store.loadOptions();
        await store.loadAttractions();
      } catch (error) {
        this.loadFailed = true;
        reportLoadError(error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loading = true;

    if(this.initialPage === 'thanks'){
      this.page = 'thanks';
    } else {
      this.syncPageWithLocation();
    }

    this.loadCatalog();

    this.$events.$on("loading", this.setLoading);

    window.addEventListener("popstate", this.syncPageWithLocation);
  },
  beforeUnmount() {
    window.removeEventListener("popstate", this.syncPageWithLocation);
    this.$events.$off("loading", this.setLoading);
  },
};
</script>
